import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Grid, Stack, Typography, Card } from '@mui/material'
import { Edit, XSquare } from 'react-feather'
import {
  AuthorizationGuard,
  CardAttribute,
  EntityPhoto,
  LinkButton,
  ModalLayout,
} from '@/components'
import { renderBehaviour } from '@/features/observationLogs/ObservationLogsTable/ObservationLogsTableBody'
import { ObservationSpecies } from './ObservationSpecies'
import {
  ObservationLogFormContainer,
  ProcessingStatusForm,
  ProcessingStatusFormData,
  TRAIL_FOUND_OPTION_VALUE,
  TRAIL_NOT_FOUND_OPTION_VALUE,
} from '@/components/forms'
import {
  getUseObservationLogsKey,
  useModal,
  useMutationWrapper,
  usePostCloneObservationLog,
  usePostObservationLogsApprove,
  usePostObservationLogsDisapprove,
  usePutObservationLogStatus,
  useDeleteObservationLog,
} from '@/hooks'
import dayjs from 'dayjs'
import { getPath } from '@/utils'
import { getSpeciesWithResearchTypesString } from '@/utils/getSpeciesWithResearchTypes'
import {
  ObservationLog,
  PostCloneObservationLog,
  SoundRecord,
  UserGroups,
} from '@/types'
import { DATE_FORMAT_DMY_TIME, PageReferrers, ROUTES_PATH } from '@/constants'
import { AutocompleteOption } from '@/components/inputs'

export type ObservationDetailsProps = {
  observationLog: ObservationLog
  showClonedItems: (ids: string) => void
}

export const Observation = ({
  observationLog,
  showClonedItems,
}: ObservationDetailsProps): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const queryClient = useQueryClient()

  const [data, setData] = useState<ObservationLog>(observationLog)

  const [isObservationLogEdit, setIsObservationLogEdit] = useState(false)

  const [isDeleteObservationLog, setIsDeleteObservationLog] = useState(false)

  const { close } = useModal()

  const {
    mutateAsync: updateObservationLogStatus,
    isLoading: mutateStatusInProcess,
  } = useMutationWrapper(
    usePutObservationLogStatus.bind(null, {
      id: data.id,
    })
  )

  const { mutateAsync: cloneObservationLog, isLoading: cloningInProcess } =
    useMutationWrapper(() => usePostCloneObservationLog({ id: data.id }))

  const { mutateAsync: approve } = useMutationWrapper(() =>
    usePostObservationLogsApprove({ id: data.id })
  )

  const { mutateAsync: disapprove } = useMutationWrapper(() =>
    usePostObservationLogsDisapprove({ id: data.id })
  )

  const { mutateAsync: deleteObservationLog } = useMutationWrapper(() =>
    useDeleteObservationLog({ id: data.id })
  )

  const handleApprove = async () => {
    const mutate = data.is_approved ? disapprove : approve

    await mutate(undefined, {
      successMessageKey: data.is_approved
        ? 'observationLogs:notifications.disapproved'
        : 'observationLogs:notifications.approved',
      onSuccess: (data) => {
        setData(data)
      },
    })
  }

  const handleDelete = async () => {
    if (isDeleteObservationLog) {
      await deleteObservationLog(undefined, {
        successMessageKey: 'observationLogs:notifications.deleted',
        onSuccess: () => {
          close()
        },
      })
    } else {
      setIsDeleteObservationLog(true)
    }
  }

  const handleStatusSubmit = async ({
    processingComment,
    assignedManager,
  }: ProcessingStatusFormData) => {
    const observationLogStatusData = {
      processing_comment: !!processingComment ? processingComment : null,
      assigned_manager_id: assignedManager?.value || null,
    }

    await updateObservationLogStatus(observationLogStatusData, {
      successMessageKey:
        'observationLogs:notifications.processingStatusChanged',
      onSuccess: (data) => {
        setData(data)
      },
    })
  }

  const updateSoundRecord = async (
    id: number,
    updatedFields: Partial<SoundRecord>
  ) => {
    setData((prevData) => ({
      ...prevData,
      sound_recordings: prevData.sound_recordings
        ? prevData.sound_recordings.map((recording) =>
            recording.id === id ? { ...recording, ...updatedFields } : recording
          )
        : [],
    }))
    const queryKey = getUseObservationLogsKey()

    await queryClient.invalidateQueries(queryKey)
  }

  const handleEditObservationLog = () => {
    setIsObservationLogEdit(!isObservationLogEdit)
  }

  const handleCloneObservationLog = async (
    cloneLog: PostCloneObservationLog
  ) => {
    if (cloneLog) {
      try {
        const clonedObservationLog = await cloneObservationLog(cloneLog)

        close()
        showClonedItems(`${data.id},${clonedObservationLog.id}`)
      } catch (error) {
        console.error(error)
      }
    }
  }

  return (
    <ModalLayout
      title={t(`observationDetailTitle`, { id: data.id })}
      cardContentSx={{ maxHeight: '70vh', overflowY: 'auto' }}
      headerAction={
        <>
          <AuthorizationGuard
            allowedGroups={[UserGroups.MANAGER, UserGroups.DATA_CONTROLLER]}
            redirect={false}
          >
            <Button
              onClick={handleApprove}
              color={data.is_approved ? 'error' : 'primary'}
            >
              {data.is_approved
                ? t('observationLogs:buttons.disapprove')
                : t('observationLogs:buttons.approve')}
            </Button>
          </AuthorizationGuard>
        </>
      }
      footerActions={
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Button onClick={() => close()}>
            {t('common:buttons.closeWindow')}
          </Button>
          <AuthorizationGuard
            allowedGroups={[UserGroups.MANAGER]}
            redirect={false}
          >
            {isDeleteObservationLog ? (
              <Stack direction="row" spacing={4} alignItems="center">
                <Typography color="red">
                  {t('observationLogs:notifications.deleteConfirmation', {
                    id: observationLog.id,
                  })}
                </Typography>
                <Button
                  onClick={handleDelete}
                  color="error"
                  variant="contained"
                >
                  {t('observationLogs:observation-log.yes')}
                </Button>
                <Button
                  onClick={() => setIsDeleteObservationLog(false)}
                  color="primary"
                  variant="contained"
                >
                  {t('observationLogs:observation-log.no')}
                </Button>
              </Stack>
            ) : (
              <Button
                onClick={handleDelete}
                color="error"
                variant="text"
                endIcon={<XSquare size={14} />}
              >
                {t('observationLogs:buttons.delete')}
              </Button>
            )}
          </AuthorizationGuard>
        </Stack>
      }
    >
      <Grid
        container={true}
        rowSpacing={8.25}
        width={{ sm: 'auto', md: 940, lg: 1200 }}
      >
        <Grid xs={12} item={true} container={true} spacing={4}>
          <Grid xs={3} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.projectNumber')}
              value={
                data.research_visit_event?.research_area.project.project_number
              }
            />
          </Grid>
          <Grid xs={6} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.projectName')}
              value={data.research_visit_event?.research_area.project.name}
            />
          </Grid>
          <Grid xs={3} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.clientName')}
              value={
                data.research_visit_event?.research_area.project.client_name
              }
            />
          </Grid>

          <Grid xs={3} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.researchAreaNumber')}
              value={data.research_visit_event?.research_area.id}
            />
          </Grid>
          <Grid xs={9} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.researchAreaLocation')}
              value={data.research_visit_event?.research_area.location_name}
            />
          </Grid>
        </Grid>

        <Grid xs={12} item={true} container={true} spacing={4}>
          <Grid xs={3} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.round')}
              value={
                <AuthorizationGuard
                  allowedGroups={[UserGroups.MANAGER]}
                  redirect={false}
                  fallback={
                    <>{`${data.research_visit_event?.round_type}
                ${data.research_visit_event?.round_number ?? ''}`}</>
                  }
                >
                  <LinkButton
                    url={getPath(ROUTES_PATH.MONITORING_DETAIL, {
                      id: data.research_visit_event.id,
                    })}
                    title={`${data.research_visit_event?.round_type}
                ${data.research_visit_event?.round_number ?? ''}`}
                    referrer={PageReferrers.OBSERVATION}
                    arrowIcon={true}
                    variant="textThin"
                    action={() => close()}
                  />
                </AuthorizationGuard>
              }
            />
          </Grid>
          <Grid xs={9} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.species')}
              value={getSpeciesWithResearchTypesString(
                data.research_visit_event
              )}
            />
          </Grid>
        </Grid>

        <Grid xs={12} item={true} container={true} spacing={4}>
          <Grid xs={3} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.observationDate')}
              value={
                data.observed_at
                  ? dayjs(data.observed_at).format(DATE_FORMAT_DMY_TIME)
                  : '-'
              }
            />
          </Grid>
          <Grid xs={3} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.createdBy')}
              value={`${data.created_by?.first_name ?? '-'} ${
                data.created_by?.last_name ?? ''
              }`}
            />
          </Grid>
          <Grid xs={6} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.researchers')}
              value={
                data.research_visit_event.researchers?.map(
                  ({ first_name, last_name }, i) =>
                    `${i > 0 ? ', ' : ''}${first_name} ${last_name}`
                ) ?? t('observation-log.noResearchers')
              }
            />
          </Grid>
          <Grid xs={3} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.updatedAt')}
              value={
                data.updated_at
                  ? dayjs(data.updated_at).format(DATE_FORMAT_DMY_TIME)
                  : '-'
              }
            />
          </Grid>
          <Grid xs={9} item={true}>
            <CardAttribute
              title={t('observation-log.fieldTitles.updatedBy')}
              value={`${data.edited_by?.first_name ?? '-'} ${
                data.edited_by?.last_name ?? ''
              }`}
            />
          </Grid>
        </Grid>

        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={4}
          width="100%"
          mt={6}
        >
          <CardAttribute
            title={t('observation-log.fieldTitles.observationType')}
            value={data.research_observation_log_type.name}
          />
          <AuthorizationGuard
            allowedGroups={[
              UserGroups.MANAGER,
              UserGroups.EMPLOYEE,
              UserGroups.SOUND_TECHNICIAN,
              UserGroups.DATA_CONTROLLER,
            ]}
            redirect={false}
          >
            <Button
              onClick={handleEditObservationLog}
              variant="textThin"
              endIcon={
                isObservationLogEdit ? (
                  <XSquare size={14} />
                ) : (
                  <Edit size={14} />
                )
              }
            >
              {isObservationLogEdit
                ? t('buttons.closeEditing')
                : t('buttons.editObservationLog')}
            </Button>
          </AuthorizationGuard>
        </Stack>

        {isObservationLogEdit ? (
          <ObservationLogFormContainer
            data={data}
            setUpdatedObservationLog={setData}
            onSuccessUpdate={() => setIsObservationLogEdit(false)}
          />
        ) : (
          <>
            <ObservationSpecies
              observationLog={data}
              setUpdatedObservationLog={setData}
              updateSoundRecord={updateSoundRecord}
              handleCloneObservationLog={handleCloneObservationLog}
              cloningInProcess={cloningInProcess}
            />

            <Grid xs={12} item={true} container={true} spacing={4}>
              {!!data.related_observation_log_id && (
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t(
                      'observation-log.fieldTitles.relatedObservationLogId'
                    )}
                    value={data.related_observation_log_id}
                  />
                </Grid>
              )}
              {!!data.direction && (
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('observation-log.fieldTitles.direction')}
                    value={data.direction}
                  />
                </Grid>
              )}
              {!!data.wind_direction && (
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('observation-log.fieldTitles.windDirection')}
                    value={data.wind_direction}
                  />
                </Grid>
              )}
              {data.amount_per_15_min !== null &&
                data.research_observation_log_type.code ===
                  'bats_flight_path' && (
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('observation-log.fieldTitles.amountPer15Min')}
                      value={data.amount_per_15_min}
                    />
                  </Grid>
                )}
              {!!data.first_fledgling_time && (
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('observation-log.fieldTitles.firstFledglingTime')}
                    value={dayjs(data.first_fledgling_time).format(
                      DATE_FORMAT_DMY_TIME
                    )}
                  />
                </Grid>
              )}
            </Grid>

            {(!!data.behaviour || !!data.other_behaviour) && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.behaviour')}
                  value={renderBehaviour(data.behaviour, data.other_behaviour)}
                />
              </Grid>
            )}
            {!!data.behaviour_notes && (
              <Grid xs={9} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.behaviourNotes')}
                  value={data.behaviour_notes}
                />
              </Grid>
            )}
            {data.is_place_found && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.placeType')}
                  value={data.place_type}
                />
              </Grid>
            )}
            {!!data.place_type_notes && (
              <Grid xs={6} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.placeTypeNotes')}
                  value={data.place_type_notes}
                />
              </Grid>
            )}
            {!!data.plant_stage && (
              <Grid xs={12} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.plantStage')}
                  value={data.plant_stage}
                />
              </Grid>
            )}
            {!!data.specie_spec && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.specieSpec')}
                  value={data.specie_spec}
                />
              </Grid>
            )}
            {!!data.specie_spec_other && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.specieSpecOther')}
                  value={data.specie_spec_other}
                />
              </Grid>
            )}
            {!!data.box_type && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.boxType')}
                  value={data.box_type}
                />
              </Grid>
            )}
            {!!data.bat_box_type && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.batBoxType')}
                  value={data.bat_box_type}
                />
              </Grid>
            )}
            {!!data.box_number && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.boxNumber')}
                  value={data.box_number}
                />
              </Grid>
            )}
            {!!data.orientation && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.orientation')}
                  value={data.orientation}
                />
              </Grid>
            )}
            {!!data.height && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.height')}
                  value={data.height}
                />
              </Grid>
            )}
            {data.research_observation_log_type.code === 'quickscan' && (
              <>
                {!!data.specie_category && (
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('observation-log.fieldTitles.specieCategory')}
                      value={data.specie_category}
                    />
                  </Grid>
                )}
                {!!data.include_or_exclude && (
                  <Grid xs={3} item={true}>
                    <CardAttribute
                      title={t('observation-log.fieldTitles.includeOrExclude')}
                      value={data.include_or_exclude}
                    />
                  </Grid>
                )}
                {!!data.explanation && (
                  <Grid xs={12} item={true}>
                    <CardAttribute
                      title={t('observation-log.fieldTitles.explanation')}
                      value={data.explanation}
                    />
                  </Grid>
                )}
              </>
            )}
            {data.is_trail_found !== undefined &&
              data.research_observation_log_type.code ===
                'physical_inspection' && (
                <Grid xs={3} item={true}>
                  <CardAttribute
                    title={t('observation-log.fieldTitles.isTrailFound')}
                    value={
                      data?.is_trail_found
                        ? TRAIL_FOUND_OPTION_VALUE
                        : data?.is_trail_found === false
                        ? TRAIL_NOT_FOUND_OPTION_VALUE
                        : ''
                    }
                  />
                </Grid>
              )}
            {data.research_observation_log_type.code ===
              'physical_inspection' &&
              !!data.trail_description && (
                <Grid xs={12} item={true}>
                  <CardAttribute
                    title={t('observation-log.fieldTitles.trailDescription')}
                    value={data.trail_description}
                  />
                </Grid>
              )}
            {data.research_observation_log_type.code ===
              'physical_inspection' && (
              <Grid xs={3} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.animalPresent')}
                  value={
                    data.animal_present
                      ? t('observation-log.yes')
                      : t('observation-log.no')
                  }
                />
              </Grid>
            )}
            {data.research_observation_log_type.code ===
              'physical_inspection' &&
              !!data.animal_present_notes && (
                <Grid xs={9} item={true}>
                  <CardAttribute
                    title={t('observation-log.fieldTitles.animalPresentNotes')}
                    value={data.animal_present_notes}
                  />
                </Grid>
              )}
            {data.research_observation_log_type.code ===
              'physical_inspection' &&
              !!data.address && (
                <Grid xs={12} item={true}>
                  <CardAttribute
                    title={t('observation-log.fieldTitles.address')}
                    value={data.address}
                  />
                </Grid>
              )}
            {!!data.notes && (
              <Grid xs={12} item={true}>
                <CardAttribute
                  title={t('observation-log.fieldTitles.notes')}
                  value={data.notes}
                />
              </Grid>
            )}
            {!!data.photos?.length && (
              <Grid xs={12} item={true} container={true} spacing={4}>
                {data.photos.map((photo) => (
                  <EntityPhoto photo={photo} key={photo.id} />
                ))}
              </Grid>
            )}
          </>
        )}

        <AuthorizationGuard
          allowedGroups={[UserGroups.MANAGER, UserGroups.DATA_CONTROLLER]}
          redirect={false}
        >
          <Grid item={true} sm={12}>
            <Card
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: 'visible',
                p: 3,
                border: '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <Typography variant="subtitle1" fontWeight="medium" mb={2}>
                {t('observation-log.fieldTitles.processingStatus')}
              </Typography>
              <ProcessingStatusForm
                defaultValues={{
                  processingComment: data.processing_comment ?? '',
                  assignedManager:
                    data && data.assigned_manager
                      ? ({
                          title: `${data.assigned_manager.first_name} ${data.assigned_manager.last_name} [${data.assigned_manager.email}]`,
                          value: data.assigned_manager.id,
                        } as AutocompleteOption<number>)
                      : null,
                }}
                onSubmit={handleStatusSubmit}
                isLoading={mutateStatusInProcess}
                processingStatusUpdatedAt={data.processing_status_updated_at}
              />
            </Card>
          </Grid>
        </AuthorizationGuard>
      </Grid>
    </ModalLayout>
  )
}
